import { useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Input, Button, Alert, Progress, Popover, PopoverHeader, PopoverBody } from "reactstrap"
import { GroupsTypeAhead, RepsTypeAhead } from "./TypeAheadComponents"
import { UpdateTaskMutation } from "../Mutations"
import StatusDropDown from "./Components"
import withApolloClient from '../../withApolloClient'
import "./EditTaskPopOver.css"
import CustomColumnInput from "./CustomColumnInput"

const ColumnQuery = gql`query ColumnQuery($columnId: ID!){
    column(id: $columnId){
        id
        title
        columnType
        isRequired
        allowedOptions
        contentType{
            id
            appLabel
            model
        }
    }
}`

function EditTaskPopOver({ isOpen, task, closeModal, target, title, name, value, type, maxLength, columnId, ...props }) {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [input, setInput] = useState({})

    useEffect(() => {
        if (value !== undefined) {
            handleInputChange(value)
        }
    }, [isOpen, task?.id, columnId, name, value])

    const { data: columnData, loading: columnLoading, error: columnError } = useQuery(
        ColumnQuery,
        {
            variables: { columnId: columnId },
            fetchPolicy: 'network-only',
            skip: !columnId,
        }
    )

    const canClearCurrentField = () => {
        if (columnId) {
            const column = columnData?.column
            return !!column && column.columnType !== "MIRROR" && !column.isRequired
        }
        return name === "dueDate" || name === "assignedToIds" || name === "groupId"
    }

    const hasCurrentFieldValue = () => {
        const currentValue = getValue()

        if (Array.isArray(currentValue)) {
            return currentValue.length > 0
        }

        return currentValue !== null && currentValue !== undefined && currentValue !== ""
    }

    const handleInputChange = (newValue) => {
        if (columnId) {
            setInput({ ...input, columnValues: [{ columnId: columnId, value: newValue }] })
            return
        } else {
            setInput({ [name]: newValue })
        }
        setError(null)
    }

    const getValue = () => {
        if (columnId) {
            const stored = input?.columnValues?.find(cv => cv.columnId === columnId)
            if (stored !== undefined) return stored.value ?? ""
            return value ?? ""
        }
        return input[name] ?? ""
    }

    const submitTaskUpdate = (queryInput) => {
        try {
            if (!props.apolloClient || !task.id) {
                throw new Error("GQL Client not found")
            }

            if (Object.keys(queryInput).length === 1) {
                return
            }
            setLoading(true)
            props.apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: queryInput },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error updating task")
                }
                if (result.data && result.data.updateTask && result.data.updateTask.ok) {
                    setError("")
                    closeModal("")
                } else {
                    let errorMessage = result.data && result.data.updateTask && result.data.updateTask.errors && result.data.updateTask.errors[0].messages ? result.data.updateTask.errors[0].messages[0] : "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error updating task " + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error updating task", error)
            setError("Error updating task" + error.toString())
            setLoading(false)
        }
    }

    const editTask = (e) => {
        e.preventDefault()
        submitTaskUpdate({ taskId: task.id, ...input })
    }

    const renderColumnInput = () => {
        if (columnLoading || columnError) {
            return <Progress animated color="info" value={100} />
        }
        return (
            <CustomColumnInput column={columnData.column} value={getValue()} onChange={handleInputChange} setError={setError} inputId={name || columnId || "task-column-input"} maxLength={maxLength ? maxLength : 200} autoFocus />
        )
    }

    return (
        <Popover trigger="legacy" placement="bottom-end" isOpen={isOpen} target={target} toggle={() => closeModal("")} className={`popover-custom-width ${name === "status" ? "status-editor-popover" : ""}`}>
            {name !== "status" && <PopoverHeader onClick={() => closeModal("")}>{title} <i style={{ float: "right" }} className="fa fa-times-circle" aria-hidden="true"> </i></PopoverHeader>}
            <PopoverBody className={name === "status" ? "status-editor-body" : ""}>
                {error && <Alert color="danger">{error}</Alert>}
                <div className='pb-2'>
                    {columnId ? (
                        renderColumnInput()
                    ) : name === "assignedToIds" ?
                        <RepsTypeAhead
                            onChange={(value) => handleInputChange(value)}
                            defaultValue={getValue()}
                            group={[]}
                            boardId={task?.board?.id || null}
                            extraRepOptions={task?.assignedTo?.edges?.map(({ node }) => node).filter(Boolean) || []}
                        />
                        : name === "groupId" ?
                            <GroupsTypeAhead
                                onChange={(value) => handleInputChange(value)}
                                defaultValue={getValue()}
                                isMulti={false}
                                placeholder="Search and select group..."
                                boardId={task?.board?.id || null}
                            />
                        : name === "status" ?
                            <StatusDropDown taskStatus={task.status} taskId={task.id} isAutomated={task.isAutomated} client={props.apolloClient} closeModal={closeModal} />
                            : type === "boolean" ? <div>
                                <span>Yes</span>
                                <div className="toggle-switch">
                                    <input type="checkbox" className="toggle-switch-checkbox" name={name} id={"taskEdit"} checked={!input[name]} onChange={() => handleInputChange(!input[name])} />
                                    <label className="toggle-switch-label" htmlFor={"branchInfo"}>
                                        <span className="toggle-switch-inner" />
                                        <span className="toggle-switch-switch" />
                                    </label>
                                </div>
                                <span>No</span>
                            </div>
                                : <Input autoComplete={"off"} type={type} maxLength={maxLength ? maxLength : 200} name={name} id={name} defaultValue={value} value={getValue()} placeholder={title} onChange={(e) => handleInputChange(e.target.value)} />
                    }
                    {loading ? (<Progress animated color="info" value={100} />) : (name === "status" || columnData?.column?.columnType === "MIRROR" ? null : (
                        <>
                            <Button className="float-right submit-button my-2 py-1" type="button" size="sm" onClick={editTask} disabled={error || loading}>Submit</Button>
                            {canClearCurrentField() && hasCurrentFieldValue() && (
                                <Button color="link" className="float-right my-2 py-1 mr-2" type="button" size="sm" onClick={() => handleInputChange(null)}>
                                    Clear
                                </Button>
                            )}
                        </>
                    ))}
                </div>
            </PopoverBody>
        </Popover>
    )
}

export default withApolloClient(EditTaskPopOver)
